import React, { useEffect, useMemo, useState } from 'react';
import {
    Activity, ArrowLeft, ArrowRight, CircleCheck, ChevronDown, Award, Download, Eye,
    Film, Flag, Timer, Users, Heart, Lightbulb, Mail, Map, MessageCircle, Play,
    Radar, Scale, Share2, Shield, Sparkles, Star, Target, User, Zap,
} from 'lucide-react';

import { CyberCharacter } from '../CyberCharacter';
import { buildMcsaRadar } from '../../../../helpers/mcsa-report.radar';
import reportData from '../../../../data/mcsa/report.json';

type TToastKey = keyof typeof reportData.toasts;
type TIconName = 'chat' | 'dial' | 'map' | 'handshake' | 'radar' | 'eye' | 'scale' | 'heart' | 'crown' | 'user' | 'shield' | 'play' | 'target' | 'film' | 'mail';

const ICONS: Record<TIconName, React.ComponentType<{ size?: number }>> = {
    chat: MessageCircle,
    dial: Timer,
    map: Map,
    handshake: Users,
    radar: Radar,
    eye: Eye,
    scale: Scale,
    heart: Heart,
    crown: Award,
    user: User,
    shield: Shield,
    play: Play,
    target: Target,
    film: Film,
    mail: Mail,
};

const CHAPTERS = reportData.chapters;
const NEXT_LABELS = reportData.nextLabels;
const CONFETTI_COLORS = ['#B284FF', '#F0B429', '#E8709A', '#4A306D', '#7C9CBF', '#3FA66B'];

const ReportIcon = ({ name, size = 20 }: { name: string; size?: number }) => {
    const Icon = ICONS[name as TIconName] || Activity;
    return <Icon size={size} />;
};

const ChapterNav = (props: { chapter: number; onBack: () => void; onNext: () => void }) => {
    const { chapter, onBack, onNext } = props;
    return (
        <div className='mcsa_atlas__chapter_nav'>
            {chapter > 0 ? (
                <button type='button' className='mcsa_atlas__nav_btn' onClick={onBack}>
                    <ArrowLeft size={16} /> Back
                </button>
            ) : <span />}
            {chapter < 7 && (
                <button type='button' className='mcsa_atlas__nav_btn mcsa_atlas__nav_btn--primary' onClick={onNext}>
                    {NEXT_LABELS[chapter]} <ArrowRight size={16} />
                </button>
            )}
        </div>
    );
};

export const AtlasReport = () => {
    const [chapter, setChapter] = useState(0);
    const [visited, setVisited] = useState<Record<number, boolean>>({ 0: true });
    const [animate, setAnimate] = useState(false);
    const [mirror, setMirror] = useState(false);
    const [openTactic, setOpenTactic] = useState<number | null>(null);
    const [openBar, setOpenBar] = useState<number | null>(null);
    const [showConfetti, setShowConfetti] = useState(true);
    const [toast, setToast] = useState('');
    const [confetti] = useState(() => Array.from({ length: 70 }, (_, index) => ({
        id: index,
        left: `${Math.random() * 100}%`,
        width: `${6 + Math.random() * 6}px`,
        height: `${9 + Math.random() * 10}px`,
        color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
        duration: `${2.6 + Math.random() * 2}s`,
        delay: `${Math.random() * 0.7}s`,
    })));

    useEffect(() => {
        const enter = window.setTimeout(() => setAnimate(true), 250);
        const hide = window.setTimeout(() => setShowConfetti(false), 3400);
        return () => {
            window.clearTimeout(enter);
            window.clearTimeout(hide);
        };
    }, []);

    const flash = (key: TToastKey) => {
        setToast(reportData.toasts[key]);
        window.setTimeout(() => setToast(''), 2600);
    };

    const goTo = (index: number) => {
        const last = index === 7 && chapter !== 7;
        setChapter(index);
        setVisited(current => ({ ...current, [index]: true }));
        setAnimate(false);
        if (index !== 4) setMirror(mirror);
        if (last) setShowConfetti(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        window.setTimeout(() => setAnimate(true), 200);
        if (last) window.setTimeout(() => setShowConfetti(false), 3600);
    };

    const radar = useMemo(
        () => buildMcsaRadar(reportData.dims.map(dim => ({ key: dim.key, v: dim.v })), animate),
        [animate],
    );

    const progress = `${(chapter / 7) * 100}%`;

    return (
        <div className='mcsa_atlas'>
            <div className='mcsa_atlas__mesh' aria-hidden='true' />
            <div className='mcsa_atlas__blob mcsa_atlas__blob--one' aria-hidden='true' />
            <div className='mcsa_atlas__blob mcsa_atlas__blob--two' aria-hidden='true' />
            <div className='mcsa_atlas__blob mcsa_atlas__blob--three' aria-hidden='true' />

            <header className='mcsa_atlas__header'>
                <div className='mcsa_atlas__header_inner'>
                    <nav className='mcsa_atlas__chips' aria-label='Report chapters'>
                    {CHAPTERS.map((item, index) => {
                        const active = index === chapter;
                        const done = visited[index] && !active;
                        return (
                            <button
                                key={item.label}
                                type='button'
                                className={`mcsa_atlas__chip${active ? ' mcsa_atlas__chip--active' : ''}${done ? ' mcsa_atlas__chip--done' : ''}`}
                                onClick={() => goTo(index)}
                            >
                                {index === 0 ? item.label : `${index}. ${item.label}`}
                            </button>
                        );
                    })}
                </nav>
                <div className='mcsa_atlas__header_actions'>
                    <button type='button' className='mcsa_atlas__icon_btn' title='Share' onClick={() => flash('share')}>
                        <Share2 size={17} />
                    </button>
                    <button type='button' className='mcsa_atlas__save' onClick={() => flash('download')}>
                        <Download size={15} /> Save
                    </button>
                </div>
                </div>
                <div className='mcsa_atlas__progress'>
                    <span style={{ width: progress }} />
                </div>
            </header>

            {showConfetti && (chapter === 0 || chapter === 7) && (
                <div className='mcsa_atlas__confetti' aria-hidden='true'>
                    {confetti.map(piece => (
                        <span
                            key={piece.id}
                            style={{
                                left: piece.left,
                                width: piece.width,
                                height: piece.height,
                                background: piece.color,
                                animationDuration: piece.duration,
                                animationDelay: piece.delay,
                            }}
                        />
                    ))}
                </div>
            )}

            {toast && <div className='mcsa_atlas__toast'>{toast}</div>}

            <main className='mcsa_atlas__main'>
                {chapter === 0 && (
                    <section className='mcsa_atlas__cover'>
                        <div className='mcsa_atlas__cover_hero'>
                            <CyberCharacter variant='welcome' />
                        </div>
                        <p className='mcsa_atlas__eyebrow'>{reportData.cover.eyebrow}</p>
                        <h1 className='mcsa_atlas__cover_title'>{reportData.cover.title}</h1>
                        <h2 className='mcsa_atlas__cover_sub'>{reportData.cover.subtitle}</h2>
                        <p className='mcsa_atlas__cover_body'>{reportData.cover.body}</p>
                        <button type='button' className='mcsa_atlas__cover_cta' onClick={() => goTo(1)}>
                            {reportData.cover.cta} <ArrowRight size={18} />
                        </button>
                        <div className='mcsa_atlas__cover_points'>
                            {reportData.cover.points.map((point, index) => (
                                <span key={point}>
                                    <i className={`mcsa_atlas__dot mcsa_atlas__dot--${index}`} />
                                    {point}
                                </span>
                            ))}
                        </div>
                    </section>
                )}

                {chapter === 1 && (
                    <section className='mcsa_atlas__chapter'>
                        <p className='mcsa_atlas__eyebrow'>JOURNEY 1 · YOU, IN MOTION</p>
                        <h2 className='mcsa_atlas__h2'>Let's explore a few moments that really stood out about you.</h2>
                        <p className='mcsa_atlas__lede'>Just three things you actually did in the journey. Read them and see if they feel familiar.</p>

                        <div className='mcsa_atlas__card'>
                            <p className='mcsa_atlas__kicker'>Explore</p>
                            <h3 className='mcsa_atlas__h3'>Your unique security pattern</h3>
                            <div className='mcsa_atlas__pattern'>
                                <div className='mcsa_atlas__pattern_copy'>
                                    <div className='mcsa_atlas__callout mcsa_atlas__callout--strength'>
                                        <span><Star size={18} fill='#F0B429' stroke='none' /> Key strength</span>
                                        <strong>{reportData.pattern.strengthName}</strong>
                                        <p>{reportData.pattern.strengthDesc}</p>
                                    </div>
                                    <div className='mcsa_atlas__callout mcsa_atlas__callout--focus'>
                                        <span><Zap size={18} fill='#D9557A' stroke='none' /> Focus area</span>
                                        <strong>{reportData.pattern.focusName}</strong>
                                        <p>{reportData.pattern.focusDesc}</p>
                                    </div>
                                </div>
                                <div className='mcsa_atlas__radar'>
                                    <svg viewBox='-30 -10 400 320'>
                                        {radar.rings.map(ring => (
                                            <polygon key={ring.points} points={ring.points} fill='none' stroke='rgba(74,48,109,0.10)' strokeWidth='1' />
                                        ))}
                                        {radar.spokes.map(spoke => (
                                            <line key={`${spoke.x}-${spoke.y}`} x1={radar.cx} y1={radar.cy} x2={spoke.x} y2={spoke.y} stroke='rgba(74,48,109,0.10)' strokeWidth='1' />
                                        ))}
                                        <polygon points={radar.avgPoints} fill='none' stroke='rgba(74,48,109,0.35)' strokeWidth='1.6' strokeDasharray='5 5' />
                                        <polygon points={radar.youPoints} fill='rgba(138,91,192,0.22)' stroke='#8A5BC0' strokeWidth='2.4' strokeLinejoin='round' />
                                        {radar.dots.map(dot => (
                                            <circle key={`${dot.x}-${dot.y}`} cx={dot.x} cy={dot.y} r='4' fill='#4A306D' />
                                        ))}
                                    </svg>
                                    {radar.labels.map(label => (
                                        <span key={label.text} className='mcsa_atlas__radar_label' style={{ left: label.left, top: label.top, transform: `translate(${label.tx}, -50%)` }}>
                                            {label.text}
                                        </span>
                                    ))}
                                    <div className='mcsa_atlas__radar_legend'>
                                        <span><i className='mcsa_atlas__you' />You</span>
                                        <span><i className='mcsa_atlas__avg' />Typical range</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='mcsa_atlas__card'>
                            <p className='mcsa_atlas__kicker'>Breakdown</p>
                            <h3 className='mcsa_atlas__h3'>What each part of the pattern means</h3>
                            {reportData.dims.map((dim, index) => (
                                <div key={dim.key} className='mcsa_atlas__bar_row'>
                                    <button type='button' onClick={() => setOpenBar(openBar === index ? null : index)}>
                                        <span>
                                            {dim.key}
                                            {dim.mark === 'star' && <Star size={16} fill='#F0B429' stroke='none' />}
                                            {dim.mark === 'bolt' && <Zap size={16} fill='#D9557A' stroke='none' />}
                                        </span>
                                        <ChevronDown size={20} className={openBar === index ? 'is-open' : ''} />
                                    </button>
                                    <div className='mcsa_atlas__track' style={{ background: dim.track }}>
                                        <span className='mcsa_atlas__fill' style={{ width: animate ? `${dim.v * 100}%` : '0%', background: dim.color }} />
                                        <span className='mcsa_atlas__knob' style={{ left: animate ? `${dim.v * 100}%` : '0%', background: dim.color }} />
                                    </div>
                                    <div className='mcsa_atlas__poles'><span>{dim.left}</span><span>{dim.right}</span></div>
                                    {openBar === index && <p className='mcsa_atlas__meaning'>{dim.meaning}</p>}
                                </div>
                            ))}
                        </div>

                        <div className='mcsa_atlas__recall'>
                            {reportData.recall.map(item => (
                                <div key={item.world} className='mcsa_atlas__recall_card'>
                                    <span style={{ background: item.gradient }}><ReportIcon name={item.icon} /></span>
                                    <div>
                                        <p style={{ color: item.tagColor }}>{item.world}</p>
                                        <p>{item.quote}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className='mcsa_atlas__note'>
                            Recognize yourself? Good. That's the point. None of this is a personality quiz- it's a pattern, drawn straight from your own choices. On the next page, we'll give the pattern a name.
                        </div>
                        <ChapterNav chapter={chapter} onBack={() => goTo(chapter - 1)} onNext={() => goTo(chapter + 1)} />
                    </section>
                )}

                {chapter === 2 && (
                    <section className='mcsa_atlas__chapter'>
                        <div className='mcsa_atlas__center'>
                            <p className='mcsa_atlas__eyebrow'>{reportData.archetype.eyebrow}</p>
                            <h2 className='mcsa_atlas__archetype'>{reportData.archetype.name}</h2>
                            <p className='mcsa_atlas__quote'>"{reportData.archetype.quote}"</p>
                            <div className='mcsa_atlas__tags'>
                                {reportData.archetype.tags.map(tag => (
                                    <span key={tag.label} style={{ color: tag.color, background: tag.bg }}>{tag.label}</span>
                                ))}
                            </div>
                        </div>
                        <div className='mcsa_atlas__card mcsa_atlas__card--narrow'>
                            <p className='mcsa_atlas__prose'>{reportData.archetype.body}</p>
                            <div className='mcsa_atlas__aside'>
                                <span><Sparkles size={18} /></span>
                                <p>{reportData.archetype.note}</p>
                            </div>
                        </div>
                        <h3 className='mcsa_atlas__h3 mcsa_atlas__center_text'>Where you sit</h3>
                        <p className='mcsa_atlas__center_copy'>Nine ways a mind can move through digital risk. None is better or worse; each has a different edge. Here's yours among them.</p>
                        <div className='mcsa_atlas__map'>
                            <span className='mcsa_atlas__axis mcsa_atlas__axis--top'>Trusting</span>
                            <span className='mcsa_atlas__axis mcsa_atlas__axis--bottom'>Suspicious</span>
                            <span className='mcsa_atlas__axis mcsa_atlas__axis--left'>Intuitive</span>
                            <span className='mcsa_atlas__axis mcsa_atlas__axis--right'>Systematic</span>
                            {reportData.mapNodes.map(node => (
                                <div
                                    key={node.name}
                                    className={`mcsa_atlas__node${node.you ? ' mcsa_atlas__node--you' : ''}`}
                                    style={{ left: `${node.x}%`, top: `${node.y}%` }}
                                >
                                    <i />
                                    <span>{node.name}</span>
                                </div>
                            ))}
                        </div>
                        <h3 className='mcsa_atlas__h3 mcsa_atlas__center_text'>Where you stand</h3>
                        <div className='mcsa_atlas__compare'>
                            {reportData.compareBars.map(bar => (
                                <div key={bar.label}>
                                    <div className='mcsa_atlas__compare_meta'><span>{bar.label}</span><span>{bar.note}</span></div>
                                    <div className='mcsa_atlas__compare_track'>
                                        <span className='mcsa_atlas__compare_band' style={{ left: `${bar.band[0]}%`, width: `${bar.band[1] - bar.band[0]}%` }} />
                                        <span className='mcsa_atlas__compare_mark' style={{ left: animate ? `${bar.mark}%` : '0%' }} />
                                    </div>
                                </div>
                            ))}
                            <p>Shaded band = where most people land. Dot = you.</p>
                        </div>
                        <div className='mcsa_atlas__unusual'>
                            <p>The unusual thing about you 🔍</p>
                            <p>{reportData.archetype.unusual}</p>
                        </div>
                        <ChapterNav chapter={chapter} onBack={() => goTo(chapter - 1)} onNext={() => goTo(chapter + 1)} />
                    </section>
                )}

                {chapter === 3 && (
                    <section className='mcsa_atlas__chapter'>
                        <p className='mcsa_atlas__eyebrow'>JOURNEY 3 · UNDER PRESSURE</p>
                        <h2 className='mcsa_atlas__h2'>How your mind actually decides when the clock is running.</h2>
                        <p className='mcsa_atlas__lede'>Five dials we read from the timed and pressured moments in your journey. Each is a tendency, not a test result. And oh, every one of them has a genuine upside.</p>
                        <div className='mcsa_atlas__gauges'>
                            {reportData.gauges.map(gauge => (
                                <div key={gauge.label} className='mcsa_atlas__card'>
                                    <div className='mcsa_atlas__gauge_head'>
                                        <span><ReportIcon name={gauge.icon} /></span>
                                        <strong>{gauge.label}</strong>
                                    </div>
                                    <div className='mcsa_atlas__track mcsa_atlas__track--purple'>
                                        <span className='mcsa_atlas__fill mcsa_atlas__fill--grad' style={{ width: animate ? `${gauge.pos}%` : '0%' }} />
                                        <span className='mcsa_atlas__knob mcsa_atlas__knob--ring' style={{ left: animate ? `${gauge.pos}%` : '0%' }} />
                                    </div>
                                    <div className='mcsa_atlas__poles mcsa_atlas__poles--accent'><span>{gauge.left}</span><span>{gauge.right}</span></div>
                                    <p>{gauge.sentence}</p>
                                </div>
                            ))}
                        </div>
                        <ChapterNav chapter={chapter} onBack={() => goTo(chapter - 1)} onNext={() => goTo(chapter + 1)} />
                    </section>
                )}

                {chapter === 4 && (
                    <section className='mcsa_atlas__chapter'>
                        <div className='mcsa_atlas__center'>
                            <p className='mcsa_atlas__eyebrow'>JOURNEY 4 · YOUR MIRROR MOMENT ⭐</p>
                            <h2 className='mcsa_atlas__h2'>The single most telling thing you did.</h2>
                            <p className='mcsa_atlas__lede'>In the <b>Online Habits</b> world you rated the same action twice; once for how risky it is, once for how likely you'd do it. Here's what you said.</p>
                        </div>
                        <div className='mcsa_atlas__card mcsa_atlas__card--wide'>
                            <p className='mcsa_atlas__mirror_prompt'>"{reportData.mirror.prompt}"</p>
                            <div className='mcsa_atlas__mirror_grid'>
                                <div className='mcsa_atlas__mirror_box mcsa_atlas__mirror_box--risk'>
                                    <p>You said it's risky</p>
                                    <div>
                                        {Array.from({ length: 5 }).map((_, index) => (
                                            <i key={index} className={index < reportData.mirror.riskFilled ? 'is-on' : ''} />
                                        ))}
                                    </div>
                                    <span>"{reportData.mirror.riskLabel}"</span>
                                </div>
                                <div className='mcsa_atlas__mirror_box mcsa_atlas__mirror_box--likely'>
                                    <p>You said you'd do it</p>
                                    <div>
                                        {Array.from({ length: 5 }).map((_, index) => (
                                            <i key={index} className={index < reportData.mirror.likelyFilled ? 'is-on' : ''} />
                                        ))}
                                    </div>
                                    <span>"{reportData.mirror.likelyLabel}"</span>
                                </div>
                            </div>
                            {!mirror && (
                                <button type='button' className='mcsa_atlas__cover_cta' onClick={() => setMirror(true)}>
                                    <Eye size={17} /> So what does that mean?
                                </button>
                            )}
                            {mirror && (
                                <div className='mcsa_atlas__mirror_reveal'>
                                    <h3>{reportData.mirror.headline}</h3>
                                    <p>{reportData.mirror.body}</p>
                                    <div className='mcsa_atlas__good'>
                                        <CircleCheck size={20} />
                                        <p>{reportData.mirror.goodNews}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <ChapterNav chapter={chapter} onBack={() => goTo(chapter - 1)} onNext={() => goTo(chapter + 1)} />
                    </section>
                )}

                {chapter === 5 && (
                    <section className='mcsa_atlas__chapter'>
                        <p className='mcsa_atlas__eyebrow'>JOURNEY 5 · ON THE RADAR</p>
                        <h2 className='mcsa_atlas__h2'>What a good manipulator would aim at you.</h2>
                        <p className='mcsa_atlas__lede'>Not to scare you, but to make you faster. These are <b>the moves built for a mind like yours.</b> Once you've seen the shape of one, you recognize it in the wild. Tap any to see it play out.</p>
                        <div className='mcsa_atlas__tactics'>
                            {reportData.tactics.map((tactic, index) => (
                                <div key={tactic.name} className='mcsa_atlas__tactic'>
                                    <button type='button' onClick={() => setOpenTactic(openTactic === index ? null : index)}>
                                        <span><ReportIcon name={tactic.icon} size={22} /></span>
                                        <div>
                                            <strong>{tactic.name} <em>{tactic.tag}</em></strong>
                                            <p>{tactic.blurb}</p>
                                        </div>
                                        <ChevronDown size={20} className={openTactic === index ? 'is-open' : ''} />
                                    </button>
                                    {openTactic === index && (
                                        <div className='mcsa_atlas__tactic_body'>
                                            <div>
                                                <p>How it would look</p>
                                                <p>{tactic.scene}</p>
                                            </div>
                                            <p><CircleCheck size={17} /> <b>The tell:</b> {tactic.tell}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <h3 className='mcsa_atlas__h3'>This has happened...and to careful people.</h3>
                        <p className='mcsa_atlas__lede'>Real cases — companies and everyday people — stripped to the human factor. Notice how none of them turned on a technical failure. They turned on someone being decent, busy, or trusting: exactly the surfaces and tactics above.</p>
                        <div className='mcsa_atlas__cases'>
                            {reportData.cases.map(item => (
                                <article key={item.title} className='mcsa_atlas__case'>
                                    <span className={`mcsa_atlas__who mcsa_atlas__who--${item.kind}`}>
                                        {item.kind === 'person' ? 'A person, like you' : 'An organisation'}
                                    </span>
                                    <div>
                                        <h4>{item.title}</h4>
                                        <time>{item.year}</time>
                                    </div>
                                    <em>{item.tactic}</em>
                                    <p>{item.human}</p>
                                    <div>
                                        <Lightbulb size={16} />
                                        <p><b>Take-away:</b> {item.lesson}</p>
                                    </div>
                                </article>
                            ))}
                        </div>
                        <ChapterNav chapter={chapter} onBack={() => goTo(chapter - 1)} onNext={() => goTo(chapter + 1)} />
                    </section>
                )}

                {chapter === 6 && (
                    <section className='mcsa_atlas__chapter'>
                        <p className='mcsa_atlas__eyebrow'>JOURNEY 6 · SHARPEN YOUR EDGE 💪</p>
                        <h2 className='mcsa_atlas__h2'>Everything that exposes you also makes you formidable.</h2>
                        <p className='mcsa_atlas__lede'>The same wiring behind your say-do gap is a genuine security asset. The kind most training can't teach. Tap any card to watch a short video on that will strengthen your mental armour and sharpen your edge immediately.</p>
                        <div className='mcsa_atlas__powers'>
                            {reportData.powers.map(power => (
                                <button type='button' key={power.title} className='mcsa_atlas__power' onClick={() => flash('playlist')}>
                                    <div className='mcsa_atlas__thumb' style={{ background: power.thumb }}>
                                        <span className='mcsa_atlas__thumb_icon'><ReportIcon name={power.icon} /></span>
                                        <span className='mcsa_atlas__play'><Play size={24} fill='#4A306D' /></span>
                                        <span className='mcsa_atlas__thumb_badge'>SHARPEN YOUR EDGE</span>
                                        <span className='mcsa_atlas__dur'>{power.dur}</span>
                                    </div>
                                    <div>
                                        <h3>{power.title}</h3>
                                        <p>{power.body}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                        <blockquote className='mcsa_atlas__quote_card'>
                            "Between stimulus and response, there is a space. In that space is our power to choose our response. In our response lies our growth and our freedom." - Victor Frankl
                            <br /><br />You're already most of the way there.
                        </blockquote>
                        <ChapterNav chapter={chapter} onBack={() => goTo(chapter - 1)} onNext={() => goTo(chapter + 1)} />
                    </section>
                )}

                {chapter === 7 && (
                    <section className='mcsa_atlas__chapter'>
                        <p className='mcsa_atlas__eyebrow'>Chapter 7 · Your next levels 🚀</p>
                        <h2 className='mcsa_atlas__h2'>Few moves to take you to build your cognitive armour.</h2>
                        <p className='mcsa_atlas__lede'>If there's nothing else you could remember from this report, remember these bullets.</p>
                        {reportData.habits.map(habit => (
                            <div key={habit.lv} className='mcsa_atlas__habit'>
                                <span><small>LV</small>{habit.lv}</span>
                                <div>
                                    <em>{habit.when}</em>
                                    <h3>{habit.title}</h3>
                                    <p>{habit.body}</p>
                                </div>
                            </div>
                        ))}
                        <div className='mcsa_atlas__hero_move'>
                            <span><Flag size={24} /></span>
                            <div>
                                <p>The hero move · your reporting reflex</p>
                                <h3>When something feels off, forward it- even 60% sure.</h3>
                                <p>Most people who spot a scam never report it. Noticing is common; telling someone is rare — and it's the whole game. You don't need to be certain. Reporting isn't a confession that you almost fell for it. It's a completed act of vigilance: <b>you noticed, and you protected everyone.</b> For a mind that helps first, this is the most natural superpower to switch on.</p>
                            </div>
                        </div>
                        <div className='mcsa_atlas__center'>
                            <h2 className='mcsa_atlas__h2'>Start building your cognitive armour.</h2>
                            <p className='mcsa_atlas__lede'>Everything here is tuned to the Empathetic Engager. Pick up wherever you like.</p>
                        </div>
                        <div className='mcsa_atlas__ctas'>
                            {reportData.ctas.map(cta => (
                                <button type='button' key={cta.title} onClick={() => flash(cta.toast as TToastKey)}>
                                    <span style={{ background: cta.gradient }}><ReportIcon name={cta.icon} /></span>
                                    <strong>{cta.title}</strong>
                                    <p>{cta.desc}</p>
                                    <em>{cta.cta} <ArrowRight size={14} /></em>
                                </button>
                            ))}
                        </div>
                        <p className='mcsa_atlas__kicker'>Cognitive security micro-training</p>
                        <h3 className='mcsa_atlas__h3'>Your playlist — each under 7 minutes.</h3>
                        <div className='mcsa_atlas__videos'>
                            {reportData.videos.map(video => (
                                <button type='button' key={video.title} onClick={() => flash('playlist')}>
                                    <div className='mcsa_atlas__thumb' style={{ background: video.thumb }}>
                                        <span className='mcsa_atlas__play'><Play size={22} fill='#4A306D' /></span>
                                        {video.badge && <span className='mcsa_atlas__vid_badge' style={{ background: video.badgeCol }}>{video.badge}</span>}
                                        <span className='mcsa_atlas__dur'>{video.dur}</span>
                                    </div>
                                    <p>{video.title}</p>
                                </button>
                            ))}
                        </div>
                        <div className='mcsa_atlas__center'>
                            <button type='button' className='mcsa_atlas__nav_btn' onClick={() => flash('training')}>
                                View the full training library <ArrowRight size={16} />
                            </button>
                        </div>
                        <div className='mcsa_atlas__close'>
                            <span><Lightbulb size={30} /></span>
                            <h2>The whole report, in one sentence.</h2>
                            <p>There's a small gap between the moment something suspicious lands in front of you and the moment you act on it. That gap is yours — and it's trainable. You already see more than most. Now you know exactly where to spend the half-second.</p>
                            <p>Welcome to your cognitive armour. 🏆</p>
                            <button type='button' className='mcsa_atlas__nav_btn' onClick={() => goTo(0)}>Back to the top</button>
                        </div>
                    </section>
                )}
            </main>
        </div>
    );
};
